// =============================================================================
// leads.ts — Schemas Zod públicos do domínio de leads.
//
// Compartilhados entre frontend (React Hook Form / listagem / validação) e
// backend (routes + service). Nenhum campo de segurança interna (cpf_hash,
// cpf_encrypted, etc.) está exposto aqui.
//
// LGPD (doc 17 §8.1):
//   phone_e164 e email são PII — cobertos por pino.redact na API.
//   cpf bruto NUNCA é armazenado em texto puro — apenas cpf_hash (HMAC).
//   cnpj é dado de PJ — texto claro, tratado com cuidado em logs (F14-S01 D1).
//
// Enums canônicos definidos aqui para evitar duplicação entre api e web.
// =============================================================================
import { z } from 'zod';
// ---------------------------------------------------------------------------
// CNPJ digit verification (F18-S09)
// ---------------------------------------------------------------------------
/**
 * Valida os dígitos verificadores de um CNPJ (algoritmo oficial Receita Federal).
 *
 * Aceita CNPJ com ou sem máscara — remove não-dígitos antes de validar.
 * Retorna false para CNPJs com todos os dígitos iguais (ex: "11111111111111"),
 * que passam na aritmética mas são conhecidamente inválidos.
 *
 * Algoritmo:
 *   1º verificador: pesos [5,4,3,2,9,8,7,6,5,4,3,2] sobre os 12 primeiros.
 *   2º verificador: pesos [6,5,4,3,2,9,8,7,6,5,4,3,2] sobre os 13 primeiros.
 *   Resto da divisão por 11 < 2 → dígito = 0; caso contrário → 11 - resto.
 *
 * @param cnpj - CNPJ com ou sem máscara (qualquer formato)
 * @returns true se os dígitos verificadores são válidos
 */
export function validateCnpjDigits(cnpj) {
    const digits = cnpj.replace(/\D/g, '');
    if (digits.length !== 14)
        return false;
    // CNPJs com todos os dígitos iguais passam na aritmética mas são inválidos.
    if (/^(\d)\1{13}$/.test(digits))
        return false;
    // Calcula um dígito verificador dado uma sequência de dígitos e pesos.
    function calcDigit(seq, weights) {
        let sum = 0;
        for (let i = 0; i < weights.length; i++) {
            // noUncheckedIndexedAccess: ambos os arrays são garantidamente alinhados pelo caller.
            sum += Number(seq[i]) * weights[i];
        }
        const rem = sum % 11;
        return rem < 2 ? 0 : 11 - rem;
    }
    const WEIGHTS_1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const WEIGHTS_2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const d1 = calcDigit(digits, WEIGHTS_1);
    const d2 = calcDigit(digits, WEIGHTS_2);
    return Number(digits[12]) === d1 && Number(digits[13]) === d2;
}
// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------
/**
 * Origens que um HUMANO pode escolher ao criar um lead.
 *
 * Não inclui `whatsapp_sync`: essa origem é escrita só pelo próprio sistema,
 * quando o vendedor pareia o aparelho e a agenda dele é importada. Oferecê-la
 * num formulário permitiria forjar a procedência de um lead.
 */
export const LeadSourceSchema = z.enum(['whatsapp', 'manual', 'import', 'chatwoot', 'api'], {
    errorMap: () => ({ message: 'source inválido' }),
});
/**
 * Origens que podem SAIR do sistema — superset do que se pode escolher.
 *
 * `whatsapp_sync` = lead trazido da agenda do aparelho do vendedor (F39). O
 * banco aceita desde a migration 0118; quem lê precisa conhecer o valor, senão
 * o Zod da resposta derruba o endpoint com 500 sobre um lead que já existe.
 *
 * A assimetria é proposital: o que o sistema PRODUZ é maior do que o que o
 * usuário ESCOLHE. Usar um enum só forçaria abrir o formulário ou esconder o
 * dado real — as duas saídas piores.
 */
export const LeadSourceOutputSchema = z.enum([
    'whatsapp',
    'manual',
    'import',
    'chatwoot',
    'api',
    'whatsapp_sync',
]);
/**
 * Canal de AQUISIÇÃO — por onde a pessoa soube do Banco do Povo.
 *
 * Distinto de `LeadSourceSchema`, que é a porta de entrada no sistema. Os dois
 * convivem: source='whatsapp' + origin_channel='instagram' é o caso comum.
 *
 * Lista curta de propósito — são os canais que a operação realmente usa hoje.
 * Crescer é um item no enum + um valor no CHECK da migration.
 */
export const LeadOriginChannelSchema = z.enum(['instagram', 'facebook', 'indicacao', 'outro'], {
    errorMap: () => ({ message: 'canal de origem inválido' }),
});
/** Rótulos em português para exibição. */
export const LEAD_ORIGIN_CHANNEL_LABELS = {
    instagram: 'Instagram',
    facebook: 'Facebook',
    indicacao: 'Indicação',
    outro: 'Outro',
};
export const LeadStatusSchema = z.enum(['new', 'qualifying', 'simulation', 'closed_won', 'closed_lost', 'archived'], { errorMap: () => ({ message: 'status inválido' }) });
// ---------------------------------------------------------------------------
// phone helpers
// ---------------------------------------------------------------------------
/** E.164 — +5511999999999 (10-15 dígitos após o +). */
const phoneE164Schema = z
    .string({ required_error: 'phone_e164 é obrigatório' })
    .regex(/^\+\d{10,15}$/, 'Telefone deve estar no formato E.164 (ex: +5511999999999)');
/**
 * Deriva phone_normalized a partir de phone_e164: strip do '+' inicial.
 * Resultado: somente dígitos, 10-15 chars.
 */
export function normalizePhone(phoneE164) {
    return phoneE164.replace(/^\+/, '');
}
// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------
/**
 * Schema base de create — objeto Zod puro sem refinamentos.
 * Exportado para permitir que LeadUpdateSchema reutilize via .omit().partial()
 * sem herdar o superRefine de email-obrigatório-no-manual (que não se aplica
 * a updates parciais, onde source pode não ser informado).
 */
export const LeadCreateBaseSchema = z.object({
    /** Nome completo do lead. LGPD: PII. */
    name: z.string({ required_error: 'name é obrigatório' }).min(1).max(255),
    /** Telefone E.164. LGPD: PII. Derivado → phone_normalized no service. */
    phone_e164: phoneE164Schema,
    /** Cidade onde o lead será atendido. */
    city_id: z.string({ required_error: 'city_id é obrigatório' }).uuid('city_id deve ser UUID'),
    /** Canal de origem. */
    source: LeadSourceSchema.default('manual'),
    /** Status inicial. Default: new. */
    status: LeadStatusSchema.optional().default('new'),
    /** Email opcional na maioria das origens. Obrigatório quando source='manual'. LGPD: PII. */
    email: z
        .string()
        .email('Email inválido')
        .max(255)
        .optional()
        .transform((v) => v ?? null)
        .nullable(),
    /**
     * CPF bruto (apenas dígitos ou com máscara) — opcional.
     * LGPD: NUNCA armazenado em texto puro.
     * O service deriva cpf_hash via hashDocument() e NÃO persiste o bruto.
     * Aceita "000.000.000-00" ou "00000000000".
     */
    cpf: z
        .string()
        .regex(/^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/, 'CPF inválido')
        .optional()
        .nullable(),
    /** Notas livres do agente. */
    notes: z.string().max(4096).optional().nullable(),
    /**
     * Canal de AQUISIÇÃO — por onde a pessoa soube do Banco do Povo.
     * Diferente de `source`, que é a porta de entrada no sistema: quem vê um post
     * no Instagram e chama no WhatsApp tem source='whatsapp' e
     * origin_channel='instagram'.
     */
    origin_channel: LeadOriginChannelSchema.optional().nullable(),
    /**
     * Distrito de residência, quando a cidade tem subdivisão.
     * Hoje só Ji-Paraná (Distrito 1 / Distrito 2).
     * LGPD: localização de pessoa natural, mesma classe de city_id.
     */
    district: z.string().max(120).optional().nullable(),
    /**
     * Metadados livres (sem PII bruta).
     * Ex: { utm_source: "google", chatwoot_contact_id: "123" }.
     */
    metadata: z.record(z.unknown()).optional().default({}),
    /** Agente responsável pelo atendimento. Opcional no create. */
    agent_id: z.string().uuid('agent_id deve ser UUID').optional().nullable(),
    /**
     * CNPJ da empresa (lead pessoa jurídica).
     * Aceita formato com máscara (00.000.000/0000-00) ou somente dígitos (14).
     * Validação inclui: formato (regex) + dígitos verificadores (algoritmo oficial Receita Federal).
     * null = lead pessoa física ou CNPJ não informado.
     *
     * F18-S09: adicionado refine com validateCnpjDigits.
     */
    cnpj: z
        .string()
        .regex(/^(\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2})$/, 'CNPJ inválido — use 14 dígitos ou formato 00.000.000/0000-00')
        .refine(validateCnpjDigits, {
        message: 'CNPJ inválido — dígitos verificadores incorretos',
    })
        .optional()
        .nullable(),
    /**
     * Razão social da empresa (lead pessoa jurídica).
     * null = lead pessoa física ou razão social não informada.
     */
    legal_name: z.string().min(1).max(255).optional().nullable(),
});
/**
 * Schema de create com regras de negócio cross-field.
 * Adiciona superRefine: email obrigatório quando source='manual' (D2 F14-S02).
 *
 * Canal interno (getOrCreateLead / LangGraph) NÃO usa este schema — opera via
 * GetOrCreateLeadInput (service.ts), que nunca tem source='manual'. O superRefine
 * não o afeta mesmo que alguém tentasse construir um LeadCreate manual para ele.
 */
export const LeadCreateSchema = LeadCreateBaseSchema.superRefine((data, ctx) => {
    // Email obrigatório no cadastro manual (D2 F14-S02).
    // Origens automáticas (whatsapp, import, chatwoot, api) coletam o email
    // progressivamente ao longo do atendimento — pode ser null no primeiro contato.
    if (data.source === 'manual' && (data.email === null || data.email === undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['email'],
            message: 'Email é obrigatório para cadastro manual',
        });
    }
});
// ---------------------------------------------------------------------------
// Update (partial)
// ---------------------------------------------------------------------------
export const LeadUpdateSchema = LeadCreateBaseSchema.omit({
    // phone_e164 não muda após criação — muda através de dedupe
    phone_e164: true,
    // city_id pode ser atualizado (transferência de cidade)
})
    .partial()
    .extend({
    city_id: z.string().uuid('city_id deve ser UUID').optional(),
})
    .refine((data) => Object.keys(data).length > 0, {
    message: 'Pelo menos um campo deve ser fornecido para update',
});
// ---------------------------------------------------------------------------
// Response (sem PII interna, sem campos de segurança)
// ---------------------------------------------------------------------------
export const LeadResponseSchema = z.object({
    id: z.string().uuid(),
    organization_id: z.string().uuid(),
    /**
     * Nullable desde F3-S01: o agente IA cria leads antes de identificar a cidade.
     * O nó identify_city preenche posteriormente via PATCH /internal/leads/:id.
     */
    city_id: z.string().uuid().nullable(),
    /** Nome da cidade (derivado via join no service). null = sem cidade. (F13-S03) */
    city_name: z.string().nullable(),
    /** ID do card no Kanban — permite mudar o estágio pelo CRM. null = sem card. (F13-S03) */
    kanban_card_id: z.string().uuid().nullable(),
    /** Estágio atual no Kanban (gestão interna). null = sem card no board. (F13-S03) */
    kanban_stage: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
    /**
     * ID do customer associado a este lead (F17-S08).
     * Não-null apenas quando o lead está em status 'closed_won' e foi convertido
     * em cliente. Deriva de customers.primary_lead_id via LEFT JOIN.
     * O frontend usa este campo para navegar à ficha do cliente (GET /customers/:id/overview).
     */
    customer_id: z
        .string()
        .uuid()
        .nullable()
        .describe('ID do customer convertido; null quando lead ainda não foi fechado'),
    agent_id: z.string().uuid().nullable(),
    name: z.string(),
    /** LGPD: PII — sempre presente para uso do frontend. Coberto por pino.redact. */
    phone_e164: z.string(),
    source: LeadSourceOutputSchema,
    status: LeadStatusSchema,
    email: z.string().nullable(),
    notes: z.string().nullable(),
    origin_channel: LeadOriginChannelSchema.nullable(),
    district: z.string().nullable(),
    metadata: z.record(z.unknown()),
    /** CNPJ da empresa (lead PJ). null = lead PF ou CNPJ não informado. */
    cnpj: z.string().nullable(),
    /** Razão social da empresa (lead PJ). null = lead PF ou não informada. */
    legal_name: z.string().nullable(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    deleted_at: z.string().datetime().nullable(),
});
// ---------------------------------------------------------------------------
// List / query
// ---------------------------------------------------------------------------
/**
 * Booleano de querystring. `z.coerce.boolean()` NÃO serve: `Boolean("false")`
 * é `true`. Só `'true'`/`'false'` literais passam; o resto é 400.
 */
const queryFlag = z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional();
/**
 * Data de calendário `YYYY-MM-DD`, interpretada no fuso da organização
 * (America/Porto_Velho) pela API. Rejeita datas inexistentes (`2026-02-31`),
 * que a aritmética de calendário rolaria em silêncio para o mês seguinte.
 */
const calendarDay = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve estar no formato AAAA-MM-DD')
    .refine((raw) => {
    const [y, m, d] = raw.split('-').map(Number);
    if (y === undefined || m === undefined || d === undefined)
        return false;
    const probe = new Date(Date.UTC(y, m - 1, d));
    return (probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d);
}, 'Data inexistente');
/**
 * Filtros da lista de leads (F58-S07 — CRM-03/CRM-05, decisão D15). A lista
 * (`GET /api/leads`) e os totais (`GET /api/leads/stats`) aceitam EXATAMENTE
 * estes filtros e montam o mesmo `where`, para os cartões do CRM baterem com
 * a lista.
 */
export const LeadListFiltersSchema = z.object({
    /**
     * Nome ou telefone (nunca CPF). Com 8+ dígitos depois de tirar a máscara,
     * casa também os dígitos do telefone — "(69) 99999-4321" acha o lead.
     */
    search: z.string().max(120).optional(),
    status: LeadStatusSchema.optional(),
    city_id: z.string().uuid().optional(),
    agent_id: z.string().uuid().optional(),
    // Filtro aceita o superset: dá para listar o que veio do aparelho do vendedor.
    source: LeadSourceOutputSchema.optional(),
    /** `true` = só leads sem responsável (`agent_id` vazio). Incompatível com `agent_id`. */
    no_agent: queryFlag,
    /**
     * `true` = só leads parados: sem interação há mais de 7 dias (ou, sem
     * nenhuma interação, criados há mais de 7 dias) — o mesmo critério do
     * cartão "parados" do dashboard.
     */
    stale: queryFlag,
    /** Criados a partir deste dia (00:00 de Rondônia), inclusive. */
    created_from: calendarDay.optional(),
    /** Criados até este dia (23:59:59.999 de Rondônia), inclusive. */
    created_to: calendarDay.optional(),
});
export const LeadListQuerySchema = LeadListFiltersSchema.extend({
    page: z.coerce.number().int().positive().default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
});
/** `GET /api/leads/stats` — mesmos filtros da lista, sem paginação. */
export const LeadStatsQuerySchema = LeadListFiltersSchema;
/**
 * Totais da lista de leads sob o MESMO escopo e os MESMOS filtros da lista.
 * `total` é igual ao `pagination.total` da lista com os mesmos filtros.
 */
export const LeadStatsResponseSchema = z.object({
    total: z.number().int().nonnegative(),
    /** Dos `total`, quantos foram criados no mês corrente (fuso de Rondônia). */
    new_in_month: z.number().int().nonnegative(),
    /** Contagem por status; todo status aparece, com 0 quando não há lead. */
    by_status: z.object({
        new: z.number().int().nonnegative(),
        qualifying: z.number().int().nonnegative(),
        simulation: z.number().int().nonnegative(),
        closed_won: z.number().int().nonnegative(),
        closed_lost: z.number().int().nonnegative(),
        archived: z.number().int().nonnegative(),
    }),
});
export const LeadListResponseSchema = z.object({
    data: z.array(LeadResponseSchema),
    pagination: z.object({
        page: z.number().int(),
        limit: z.number().int(),
        total: z.number().int(),
        totalPages: z.number().int(),
    }),
});
//# sourceMappingURL=leads.js.map